// Supplier Category and Specialty Settings
import { db } from './firebase';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';

// Default supplier categories and specialties
export const defaultSupplierSettings = {
  metadata: {
    version: "1.0.0",
    lastUpdated: null,
    createdBy: "system"
  },
  categories: [
    'upholstery',
    'delivery', 
    'fabric',
    'furniture',
    'installation',
    'design',
    'consultation',
    'restoration',
    'lighting',
    'custom_work',
    'other'
  ],
  specialties: [
    'Custom Furniture',
    'Fabric Selection', 
    'Leather Work',
    'Antique Restoration',
    'Modern Design',
    'Eco-Friendly Materials',
    'Rush Delivery',
    'Installation',
    'Consultation',
    'Bulk Orders',
    'Commercial Projects',
    'Residential',
    'Lampshade Making',
    'Reupholstery',
    'Furniture Refinishing',
    'Outdoor Fabrics',
    'Custom Cushions',
    'Storage Solutions',
    'Timber Work',
    'Metal Work'
  ]
};

// Initialize supplier settings
export const initializeSupplierSettings = async () => {
  try {
    const settingsRef = doc(db, 'settings', 'supplierCategories');
    const settingsDoc = await getDoc(settingsRef);
    
    if (!settingsDoc.exists()) {
      const settingsData = {
        ...defaultSupplierSettings,
        metadata: {
          ...defaultSupplierSettings.metadata,
          initialized: serverTimestamp()
        }
      };
      
      await setDoc(settingsRef, settingsData);
      console.log('Supplier settings initialized');
      return settingsData;
    }
    
    return settingsDoc.data();
  } catch (error) {
    console.error('Error initializing supplier settings:', error);
    throw error;
  }
};

// Get current supplier settings
export const getSupplierSettings = async () => {
  try {
    const settingsRef = doc(db, 'settings', 'supplierCategories');
    const settingsDoc = await getDoc(settingsRef);
    
    if (settingsDoc.exists()) {
      return settingsDoc.data();
    }
    
    // Initialize if doesn't exist
    return await initializeSupplierSettings();
  } catch (error) {
    console.error('Error getting supplier settings:', error);
    throw error;
  }
};

// Update supplier settings
export const updateSupplierSettings = async (newSettings) => {
  try {
    const settingsRef = doc(db, 'settings', 'supplierCategories');
    const updateData = {
      ...newSettings,
      metadata: {
        ...newSettings.metadata,
        lastUpdated: serverTimestamp()
      }
    };
    
    await setDoc(settingsRef, updateData);
    console.log('Supplier settings updated successfully');
    return updateData;
  } catch (error) {
    console.error('Error updating supplier settings:', error);
    throw error;
  }
};

// Add new category
export const addSupplierCategory = async (category) => {
  try {
    const settings = await getSupplierSettings();
    if (!settings.categories.includes(category)) {
      settings.categories.push(category);
      return await updateSupplierSettings(settings);
    }
    return settings;
  } catch (error) {
    console.error('Error adding supplier category:', error);
    throw error;
  }
};

// Remove category
export const removeSupplierCategory = async (category) => {
  try {
    const settings = await getSupplierSettings();
    settings.categories = settings.categories.filter(cat => cat !== category);
    return await updateSupplierSettings(settings);
  } catch (error) {
    console.error('Error removing supplier category:', error);
    throw error;
  }
};

// Add new specialty
export const addSupplierSpecialty = async (specialty) => {
  try {
    const settings = await getSupplierSettings();
    if (!settings.specialties.includes(specialty)) {
      settings.specialties.push(specialty);
      return await updateSupplierSettings(settings);
    }
    return settings;
  } catch (error) {
    console.error('Error adding supplier specialty:', error);
    throw error;
  }
};

// Remove specialty
export const removeSupplierSpecialty = async (specialty) => {
  try {
    const settings = await getSupplierSettings();
    settings.specialties = settings.specialties.filter(spec => spec !== specialty);
    return await updateSupplierSettings(settings);
  } catch (error) {
    console.error('Error removing supplier specialty:', error);
    throw error;
  }
};